import { normalizeVenueAliasUrl } from 'helpers/consumer-urls';
import { formatOrderId } from 'helpers/sku';

import dashboardMessages from './SkuOrderDashboard.messages';
import { ORDER_ITEM_CANCELLATION_TYPE } from './SkuOrders.constants';
import {
  formatItemGroupsForFlyout,
  formatItemGroupsForTable,
  formatLifecycleStatusDescriptions,
  formatModifierLinesForItemGroup,
  formatOrderDate,
  formatOrderTime,
  formatOrderTotalsForFlyout,
  getOrderItemId,
  getOrderItemName,
  isCancelledOrderItem,
  mapOrderToFlyoutData,
} from '../shared/SkuOrderFlyout.helpers';

export {
  formatOrderDate,
  formatOrderTime,
  formatItemGroupsForFlyout,
  formatItemGroupsForTable,
  formatLifecycleStatusDescriptions,
  formatModifierLinesForItemGroup,
  mapOrderToFlyoutData,
};

// Sanitization to avoid HTML injection
const escapeHtml = str => str?.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') ?? '';

const safeUrl = href => {
  if (!href) return null;
  try {
    const parsed = new URL(href);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : null;
  } catch {
    return null;
  }
};

const wrapStrong = value => `<strong>${value}</strong>`;

const wrapAnchor = (text, href) => {
  const url = safeUrl(href);
  return url ? `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>` : text;
};

export const formatOrderItemsSummary = orderItems => {
  if (!orderItems?.length) return null;

  const groupedOrderItems = orderItems.reduce((memo, orderItem) => {
    const itemId = getOrderItemId(orderItem);

    if (!itemId) return memo;

    const currentGroup = memo[itemId] || [];
    return {
      ...memo,
      [itemId]: currentGroup.concat(orderItem),
    };
  }, {});

  const summary = Object.values(groupedOrderItems)
    .map(group => {
      const hasVisibleItem = group.some(orderItem => !isCancelledOrderItem(orderItem));
      const visibleGroup = hasVisibleItem ? group.filter(orderItem => !isCancelledOrderItem(orderItem)) : group;
      const [displayOrderItem] = visibleGroup;
      const displayName = getOrderItemName(displayOrderItem);

      if (!displayName) return null;

      return visibleGroup.length > 1 ? `${displayName} (x${visibleGroup.length})` : displayName;
    })
    .filter(Boolean)
    .join(', ');

  return summary || null;
};

export const getOrderItemNames = orderItems => {
  if (!orderItems?.length) return '';
  const names = [...new Set(orderItems.map(item => item?.core?.itemSummary?.name).filter(Boolean))];
  return names.join(', ');
};

export const getFirstOrderItemName = orderItems => {
  if (!orderItems?.length) return '';
  return orderItems.find(item => item?.core?.itemSummary?.name)?.core?.itemSummary?.name || '';
};

const CANCEL_MESSAGE_BY_CANCELLATION_TYPE = {
  [ORDER_ITEM_CANCELLATION_TYPE.PARTNER_SOLD_OUT]: dashboardMessages.cancelOrderSoldOutMessage,
};

export const buildCancelMessageHtml = (
  intl,
  {
    time,
    date,
    venueName,
    userName = '',
    venueAliasUrl,
    shareUrlConsumerVenue,
    creditAmountTotal = null,
    cancellationType = null,
  }
) => {
  const safeVenueName = escapeHtml(venueName);
  const safeUserName = escapeHtml(userName);
  const greeting = safeUserName ? `Hi ${safeUserName},` : 'Hi, ';
  const dateTimePart = `${escapeHtml(time)}, ${escapeHtml(date)}`;
  const creditsRefundPhrase =
    creditAmountTotal != null
      ? intl.formatMessage(dashboardMessages.cancelOrderCreditsRefundWithAmount, { creditAmountTotal })
      : intl.formatMessage(dashboardMessages.cancelOrderCreditsRefundWithoutAmount);
  const htmlValues = {
    greeting,
    venueName: safeVenueName,
    dateTimeHtml: wrapStrong(dateTimePart),
    creditsRefundPhrase,
    reorderNowLink: wrapAnchor('Reorder now', normalizeVenueAliasUrl(venueAliasUrl, shareUrlConsumerVenue)),
  };

  const message = CANCEL_MESSAGE_BY_CANCELLATION_TYPE[cancellationType] || dashboardMessages.cancelOrderMessage;

  return intl.formatMessage(message, htmlValues);
};

export const mapOrderToTableRow = (order, { intl, venueTimezone, locale }) => ({
  id: formatOrderId(order?.core?.orderId),
  rawOrderId: order?.core?.orderId || null,
  memberName: order?.userName || null,
  userId: order?.core?.userId || null,
  venueName: order?.venueName || null,
  createdOn: order?.core?.createdOn || null,
  items: formatOrderItemsSummary(order?.orderItems),
  itemGroups: formatItemGroupsForTable(order?.orderItems, order?.lifecycleStatus),
  payableAmountDisplay: formatOrderTotalsForFlyout(order, intl).totalDisplay,
  pickupDate: formatOrderDate(
    order?.core?.requestedPickupTime || order?.core?.markedFulfilledAt,
    venueTimezone,
    locale
  ),
  pickupTime: formatOrderTime(
    order?.core?.requestedPickupTime || order?.core?.markedFulfilledAt,
    venueTimezone,
    locale
  ),
  lifecycleStatus: order?.lifecycleStatus,
});
